import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import { useTerminologia } from '../hooks/useTerminologia';

const HOST_API = process.env.REACT_APP_URL;

const fmtFecha = (fechaStr) => {
    if (!fechaStr) return '';
    return new Date(fechaStr).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtMonto = (monto) => (monto ?? 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });

const ESTADO_COLORS = {
    'Pendiente':  { bg: '#fffaeb', color: '#b54708', border: '#fedf89' },
    'Cancelada':  { bg: '#f2f4f7', color: '#667085', border: '#d0d5dd' },
    'Finalizada': { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5' },
};

const estadoBadge = (estado) => {
    const s = ESTADO_COLORS[estado] || { bg: '#f2f4f7', color: '#667085', border: '#d0d5dd' };
    return (
        <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontSize: '0.78rem', fontWeight: 700, padding: '0.1rem 0.55rem', borderRadius: 99 }}>
            {estado}
        </span>
    );
};

const InfraccionesBarrio = () => {
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();
    const t = useTerminologia();

    const usuarioId = (() => { try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.id || u.usuarioId || 0; } catch { return 0; } })();

    const [ufs, setUfs] = useState([]);
    const [ufSel, setUfSel] = useState(null);
    const [infracciones, setInfracciones] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!barrio?.id || !usuarioId) return;
        axios.get(`${HOST_API}api/unidadesFuncionales/GetUFsByUsuario?usuarioId=${usuarioId}&barrioId=${barrio.id}`)
            .then(({ data }) => {
                setUfs(data ?? []);
                setUfSel((prev) => prev ?? (data?.[0]?.unidadFuncionalId ?? null));
            })
            .catch(() => {});
    }, [barrio?.id, usuarioId]);

    const fetchInfracciones = useCallback(async () => {
        if (!ufSel) return;
        setLoading(true);
        try {
            const { data } = await axios.get(`${HOST_API}api/infracciones/GetInfraccionesPorUnidadFuncional`, {
                params: { administracionId, unidadFuncionalId: ufSel },
            });
            setInfracciones(data ?? []);
        } catch {
            setInfracciones([]);
        } finally {
            setLoading(false);
        }
    }, [ufSel, administracionId]);

    useEffect(() => { fetchInfracciones(); }, [fetchInfracciones]);

    const ufLabel = useMemo(() => ufs.find(u => u.unidadFuncionalId === ufSel)?.unidadFuncional ?? '', [ufs, ufSel]);

    return (
        <>
            <h4>Infracciones</h4>
            {ufLabel && <p style={{ color: '#667085', fontSize: '0.9rem', marginTop: '-0.5rem' }}>{t.UnidadFuncional}: {ufLabel}</p>}
            <br />

            {ufs.length > 1 && (
                <div className="flex align-items-center gap-3 mb-4">
                    <Dropdown
                        value={ufSel}
                        options={ufs}
                        optionLabel="unidadFuncional"
                        optionValue="unidadFuncionalId"
                        onChange={(e) => setUfSel(e.value)}
                        className="p-inputtext-sm"
                        style={{ minWidth: 220 }}
                    />
                </div>
            )}

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <ProgressSpinner style={{ width: 48, height: 48 }} strokeWidth="4" />
                </div>
            ) : infracciones.length === 0 ? (
                <div style={{ background: '#f9fafb', border: '1px dashed #e4e7ec', borderRadius: 12, padding: '2rem', textAlign: 'center', color: '#98a2b3' }}>
                    No hay infracciones registradas
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {infracciones.map(i => (
                        <div key={i.infraccionId} style={{
                            background: '#fff',
                            border: '1px solid #e4e7ec',
                            borderLeft: `4px solid ${(ESTADO_COLORS[i.estado] || {}).color || '#dc6803'}`,
                            borderRadius: 12,
                            padding: '1rem 1.1rem',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
                                <span style={{ fontWeight: 700, color: '#101828', fontSize: '0.95rem' }}>{fmtMonto(i.monto)}</span>
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <span style={{ fontSize: '0.78rem', color: '#667085' }}>{fmtFecha(i.fecha)}</span>
                                    {estadoBadge(i.estado)}
                                </div>
                            </div>
                            {i.motivo && <p style={{ margin: 0, fontSize: '0.85rem', color: '#344054', whiteSpace: 'pre-wrap' }}>{i.motivo}</p>}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

export default InfraccionesBarrio;
